/* eslint-disable no-useless-escape --
 * La barra invertida de `<\/script>` es deliberada: la secuencia </script> sin
 * escapar cerraria el bloque antes de tiempo si este archivo quedara incrustado
 * en una pagina.
 */

import { ESTILOS_VISTA_PREVIA, barraVistaPrevia, abrirVistaPrevia } from './vistaPrevia';

/**
 * El listado de artículos, para imprimir en hoja.
 *
 * A diferencia del comprobante, este documento **sí** va en A4: es una lista de
 * cientos de renglones que se lleva al depósito para contar, o se le acerca al
 * proveedor para pedir. En papel de 80 mm no se puede trabajar con ella.
 *
 * Lleva los totales arriba y no al final. Una lista de inventario se imprime
 * para usarla, y quien la usa necesita el resumen sin tener que buscar la
 * última hoja.
 */

const escapar = (texto) => String(texto ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const numero = (n) => Math.round(parseFloat(n) || 0).toLocaleString('es-PY');

const fecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-PY') : '—');

export function buildListaProductosHtml(reporte, settings, { conBarra = true } = {}) {
    const moneda = settings?.moneda || 'Gs.';
    const negocio = settings?.nombre_negocio || 'Mi Ferretería';
    const generado = new Date(reporte?.generado || Date.now()).toLocaleString('es-PY');

    const filas = (reporte?.productos || []).map(p => `
    <tr class="${p.a_reponer ? 'reponer' : ''}">
      <td class="mono">${escapar(p.codigo_barras)}</td>
      <td>
        ${escapar(p.nombre)}
        ${p.marca ? `<span class="tenue"> · ${escapar(p.marca)}</span>` : ''}
        ${!p.activo ? '<span class="baja"> (dado de baja)</span>' : ''}
      </td>
      <td>${escapar(p.categoria) || '—'}</td>
      <td class="cen">${p.tasa_iva === 0 ? 'Ex.' : `${p.tasa_iva}%`}</td>
      <td class="der">${numero(p.costo_compra)}</td>
      <td class="der">${numero(p.precio_venta)}</td>
      <td class="cen ${p.a_reponer ? 'negrita' : ''}">${p.stock_actual}</td>
      <td class="cen tenue">${p.stock_minimo}</td>
      <td class="der">${numero(p.valor_costo)}</td>
      <td class="cen tenue">${fecha(p.ultima_venta)}</td>
    </tr>`).join('');

    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Listado de artículos — ${escapar(negocio)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #000; padding: 10mm; }

  h1 { font-size: 15px; text-transform: uppercase; }
  .sub { font-size: 10px; color: #444; margin-top: 2px; }
  header { border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 8px; }

  .resumen { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
  .dato { border: 1px solid #ccc; padding: 5px 10px; min-width: 110px; }
  .dato .et { font-size: 8px; text-transform: uppercase; letter-spacing: .4px; color: #555; }
  .dato .va { font-size: 13px; font-weight: bold; }

  table { width: 100%; border-collapse: collapse; font-size: 9px; }
  thead { display: table-header-group; }  /* la cabecera se repite en cada hoja */
  th { border-bottom: 1.5px solid #000; padding: 3px 4px; text-align: left;
       text-transform: uppercase; font-size: 8px; }
  td { border-bottom: 1px solid #e5e5e5; padding: 3px 4px; }
  tr { page-break-inside: avoid; }
  .der { text-align: right; }
  .cen { text-align: center; }
  .mono { font-family: 'Courier New', monospace; }
  .tenue { color: #777; }
  .negrita { font-weight: bold; }
  .baja { color: #999; font-style: italic; }
  /* Lo que hay que reponer se marca con una barra al costado y no con color de
     fondo: la mayoría de las ferreterías imprime en blanco y negro, y un fondo
     gris claro desaparece. */
  tr.reponer td:first-child { border-left: 3px solid #000; }
  tr.reponer { background: #f7f7f7; }

  footer { margin-top: 10px; font-size: 8px; color: #666; text-align: center; }

  @media print { @page { size: A4 portrait; margin: 10mm; } body { padding: 0; } }
${conBarra ? ESTILOS_VISTA_PREVIA : ''}
</style>
</head>
<body>
${conBarra ? barraVistaPrevia('Listado de artículos') : ''}

<header>
  <h1>${escapar(negocio)} — Listado de artículos</h1>
  <div class="sub">Generado el ${escapar(generado)}</div>
</header>

<div class="resumen">
  <div class="dato"><div class="et">Artículos</div><div class="va">${reporte?.total_articulos ?? 0}</div></div>
  <div class="dato"><div class="et">Unidades en stock</div><div class="va">${numero(reporte?.unidades_en_stock)}</div></div>
  <div class="dato"><div class="et">A reponer</div><div class="va">${reporte?.a_reponer ?? 0}</div></div>
  <div class="dato"><div class="et">Sin stock</div><div class="va">${reporte?.sin_stock ?? 0}</div></div>
  <div class="dato"><div class="et">Valor al costo</div><div class="va">${moneda} ${numero(reporte?.valor_total_costo)}</div></div>
  <div class="dato"><div class="et">Valor a precio de venta</div><div class="va">${moneda} ${numero(reporte?.valor_total_venta)}</div></div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:70px;">Código</th>
      <th>Artículo</th>
      <th style="width:80px;">Categoría</th>
      <th class="cen" style="width:30px;">IVA</th>
      <th class="der" style="width:60px;">Costo</th>
      <th class="der" style="width:60px;">Venta</th>
      <th class="cen" style="width:36px;">Stock</th>
      <th class="cen" style="width:30px;">Mín.</th>
      <th class="der" style="width:70px;">Valor costo</th>
      <th class="cen" style="width:60px;">Últ. venta</th>
    </tr>
  </thead>
  <tbody>${filas}</tbody>
</table>

<footer>Los artículos marcados con una barra al costado están en su stock mínimo o por debajo.</footer>
</body>
</html>`;
}

export function openListaProductos(reporte, settings) {
    return abrirVistaPrevia(buildListaProductosHtml(reporte, settings), { ancho: 1000, alto: 760 });
}
